import React, { useState, useEffect } from 'react';
import { X, Edit2 } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { formatTextWithLinks } from '../../../../utils/formatters';

interface EditLotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  lot: {
    lot_id: string;
    community_name: string | null;
    lot_perm_use: string | null;
    lot_zone: string | null;
    zone_desc?: string | null;
    lot_desc: string | null;
    leaselot_lease: number | null;
    leaselot_lot: string | null;
    leaselot_use: string | null;
    holder_name: string | null;
    lot_avail?: string;          // Y/N for availability
    lot_surr?: string;           // Y/N for surrendered
    lot_surr_note?: string | null; // Surrender notes
    lot_note?: string | null;    // General lot notes
  };
}

export default function EditLotModal({ isOpen, onClose, onSuccess, lot }: EditLotModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    lotAvail: lot.lot_avail || 'N',
    lotSurr: lot.lot_surr || 'N',
    lotSurrNote: lot.lot_surr_note || '',
    permittedUse: lot.lot_perm_use || '',
    description: lot.lot_desc || '',
    lotNote: lot.lot_note || ''
  });

  useEffect(() => {
    if (isOpen) {
      // Reset form data when modal opens
      setFormData({
        lotAvail: lot.lot_avail || 'N',
        lotSurr: lot.lot_surr || 'N',
        lotSurrNote: lot.lot_surr_note || '',
        permittedUse: lot.lot_perm_use || '',
        description: lot.lot_desc || '',
        lotNote: lot.lot_note || ''
      });
      setError(null);
    }
  }, [isOpen, lot]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const { error: updateError } = await supabase
        .from('land_lots')
        .update({
          lot_avail: formData.lotAvail,
          lot_surr: formData.lotSurr,
          lot_surr_note: formData.lotSurrNote || null,
          lot_perm_use: formData.permittedUse || null,
          lot_desc: formData.description || null,
          lot_note: formData.lotNote || null,
          modified_by: 'system', // This would ideally be the current user
          modified_date: new Date().toISOString()
        })
        .eq('lot_id', lot.lot_id);

      if (updateError) throw updateError;

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating lot:', err);
      setError(err instanceof Error ? err.message : 'Failed to update lot');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-8">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center">
            <Edit2 className="h-5 w-5 mr-2 text-primary" />
            <h3 className="text-lg font-medium">Edit Lot</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-md mb-4">
              {error}
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Basic Information</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500">
                  Lot ID
                </label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {lot.lot_id}
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500">
                  Community
                </label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {lot.community_name ?? 'N/A'}
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500">
                  Zone
                </label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {lot.zone_desc ?? lot.lot_zone ?? 'Not specified'}
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500">
                  Lot Available
                </label>
                <div className="mt-1 flex space-x-2">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, lotAvail: 'Y' }))}
                    className={`px-4 py-2 border text-sm font-medium rounded-md ${
                      formData.lotAvail === 'Y'
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, lotAvail: 'N' }))}
                    className={`px-4 py-2 border text-sm font-medium rounded-md ${
                      formData.lotAvail === 'N'
                        ? 'bg-red-50 text-red-700 border-red-200'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500">
                  Lot Surrendered
                </label>
                <div className="mt-1 flex space-x-2">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, lotSurr: 'Y' }))}
                    className={`px-4 py-2 border text-sm font-medium rounded-md ${
                      formData.lotSurr === 'Y'
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, lotSurr: 'N' }))}
                    className={`px-4 py-2 border text-sm font-medium rounded-md ${
                      formData.lotSurr === 'N'
                        ? 'bg-red-50 text-red-700 border-red-200'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>

              <div className="col-span-2">
                <label className="block text-xs text-gray-500">
                  Surrender Notes
                </label>
                <textarea
                  value={formData.lotSurrNote}
                  onChange={(e) => setFormData(prev => ({ ...prev, lotSurrNote: e.target.value }))}
                  className="mt-1 p-2 bg-white border border-gray-300 rounded-md text-gray-700 text-sm w-full"
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Lease Details */}
          <div className="space-y-4">
            <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Lease Details</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500">
                  Lease ID
                </label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {lot.leaselot_lease ?? 'Not specified'}
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500">
                  Leaseholder
                </label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {lot.holder_name ?? 'Not specified'}
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500">
                  Lease Lot ID
                </label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {lot.leaselot_lot ?? 'Not specified'}
                </div>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="space-y-4">
            <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Additional Information</h4>
            
            <div>
              <label className="block text-xs text-gray-500">
                Permitted Use
              </label>
              <textarea
                value={formData.permittedUse}
                onChange={(e) => setFormData(prev => ({ ...prev, permittedUse: e.target.value }))}
                className="mt-1 p-2 bg-white border border-gray-300 rounded-md text-gray-700 text-sm w-full"
                rows={2}
              />
            </div>

            <div>
              <label className="block text-xs text-gray-500">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="mt-1 p-2 bg-white border border-gray-300 rounded-md text-gray-700 text-sm w-full"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-xs text-gray-500">
                Lot Notes
              </label>
              <textarea
                value={formData.lotNote}
                onChange={(e) => setFormData(prev => ({ ...prev, lotNote: e.target.value }))}
                className="mt-1 p-2 bg-white border border-gray-300 rounded-md text-gray-700 text-sm w-full"
                rows={3}
              />
            </div>
          </div>
        </form>

        <div className="p-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 mr-2"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
